import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
// import './styles.css';


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PrivateChat error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1 style={{ color: 'red', textAlign: 'center', padding: '20px' }}>Something went wrong in chat view. Check console.</h1>;
    }
    return this.props.children;
  }
}



// Generate random AES-256 key and export as base64
const generateKey = async () => {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const exported = await crypto.subtle.exportKey('raw', key);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

// Import base64 string back to CryptoKey – safer version
const importKey = async (base64Key) => {
  if (!base64Key || typeof base64Key !== 'string' || base64Key.trim() === '') {
    console.warn('importKey called with empty/invalid base64');
    return null;
  }
  try {
    const raw = Uint8Array.from(atob(base64Key.trim()), c => c.charCodeAt(0));
    if (raw.length !== 32) {
      console.warn('Invalid key length:', raw.length);
      return null;
    }
    return await crypto.subtle.importKey(
      'raw',
      raw,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  } catch (err) {
    console.error('Invalid key format:', err);
    return null;
  }
};

function PrivateChat() {
  const { chatId } = useParams();
  console.log('PrivateChat component started rendering');

  const [messages, setMessages] = useState([]);

  const [myUsername, setMyUsername] = useState(null);
  const [decryptedMessages, setDecryptedMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [cryptoKey, setCryptoKey] = useState(null);
  const [sharedKeyInput, setSharedKeyInput] = useState('');
  const [keyStatus, setKeyStatus] = useState('loading');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [chatNameInput, setChatNameInput] = useState('');
  const [showPassphraseInput, setShowPassphraseInput] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [passphraseError, setPassphraseError] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [lifespanHours, setLifespanHours] = useState('24');
  const messagesEndRef = useRef(null);
  const [isReloading, setIsReloading] = useState(false);
  const wasAtBottomRef = useRef(true); // track if user was at bottom before update

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteLink, setInviteLink] = useState('');

  const { search } = useLocation();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [inviteKey, setInviteKey] = useState(queryParams.get('key'));

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');

  const [blockedChats, setBlockedChats] = useState(() => {
    const stored = localStorage.getItem('blocked_chats');
    return stored ? JSON.parse(stored) : [];
  });
  const [errorBanner, setErrorBanner] = useState('');


  const handleSendMessage = async () => {
    console.log("handleSendMessage STARTED - real function");

    const text = newMessage.trim();
    if (!text) {
      console.log("Aborted: empty text");
      return;
    }
    console.log("Text OK:", text);

    if (!cryptoKey) {
      console.log("Aborted: no cryptoKey");
      alert("No encryption key - cannot send");
      return;
    }
    console.log("cryptoKey OK");

    setNewMessage('');

    let base64;
    try {
      console.log("Encryption START");
      // YOUR ENCRYPTION CODE HERE (keep as is)
      const encoder = new TextEncoder();
      const encodedMessage = encoder.encode(text);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encryptedBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        encodedMessage
      );
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const combined = new Uint8Array(iv.length + encryptedArray.length);
      combined.set(iv);
      combined.set(encryptedArray, iv.length);
      base64 = btoa(String.fromCharCode(...combined));
      console.log("Encryption SUCCESS - length:", base64.length);
    } catch (err) {
      console.error("ENCRYPTION CRASH:", err.message, err.stack);
      alert("Encryption failed - check console");
      setNewMessage(text);
      return;
    }

    const optimisticMsg = {
      id: `local-${Date.now()}`,
      chatId,
      encrypted: base64,
      created_at: new Date().toISOString(),
      sender: 'me',
      text
    };

    console.log("Optimistic created");

    setMessages(prev => [...prev, optimisticMsg]);
    setDecryptedMessages(prev => [...prev, optimisticMsg]);

    localStorage.setItem(`messages_${chatId}`, JSON.stringify([...messages, optimisticMsg]));

    console.log("Optimistic added - POST attempt");

    try {
      console.log("POST START");
      const res = await fetch('https://i-msgnet-backend-production.up.railway.app/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          chatId,
          encrypted: base64,
          lifespanHours: lifespanHours || 24
        })
      });

      console.log("POST RETURNED - status:", res.status);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error("Server error:", res.status, errorData);
        alert(`Send failed: ${res.status}`);
        return;
      }

      console.log("POST SUCCESS");
      pollMessages();

    } catch (err) {
      console.error("POST catch:", err.message, err.stack);
      alert("Network send error");
    }
  };


  useEffect(() => {
    if (!chatId) return;

    console.log("Loading localStorage for chat:", chatId);

    const saved = localStorage.getItem(`messages_${chatId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log("Loaded", parsed.length, "saved messages");
        setMessages(parsed);
        setDecryptedMessages(parsed);
      } catch (e) {
        console.error("LocalStorage load error:", e);
      }
    }
  }, [chatId]);




  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !myUsername) {
      try {

        if (messages.length > 0 && msg === messages[0]) {
          console.log("Backend message fields:", Object.keys(msg));
          console.log("Sender-related field value:", msg.sender_username || msg.username || msg.sender || '[none]');
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        setMyUsername(payload.username || payload.sub || payload.name || payload.user);

        if (!myUsername && payload) {
          setMyUsername(payload.sub || payload.name || payload.userId || 'fallback-user');
        }

        console.log("Detected my username:", payload.username || payload.sub);
      } catch (err) {
        console.warn("Failed to parse username from token", err);
      }
    }
  }, []);


  console.log('States initialized - chatId:', chatId);
  console.log('inviteKey from URL:', inviteKey);
  console.log('token from localStorage:', localStorage.getItem('token'));

  // Improved timestamp formatting
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const timeStr = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    if (isToday) return timeStr;
    let datePart = date.toLocaleDateString('fi-FI', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit'
    });
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 7) datePart += `.${date.getFullYear()}`;
    return `${datePart} ${timeStr}`;
  };

  // Load messages
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(`messages_${chatId}`)) || [];
    setMessages(stored);
  }, [chatId]);

  // Show name prompt if chat has no name yet
  useEffect(() => {
    const storedChats = JSON.parse(localStorage.getItem('chats')) || [];
    const existing = storedChats.find(c => c.id === chatId);
    if (!existing) setShowNamePrompt(true);
  }, [chatId]);

  // Load/use key
  useEffect(() => {
    (async () => {
      const storedKey = localStorage.getItem(`key_${chatId}`);
      let key = null;
      if (storedKey) {
        key = await importKey(storedKey);
        setKeyStatus(key ? 'shared' : 'invalid');
      } else {
        const encoder = new TextEncoder();
        const material = await crypto.subtle.importKey(
          'raw',
          encoder.encode(chatId + 'fixed-salt-for-poc'),
          { name: 'PBKDF2' },
          false,
          ['deriveBits', 'deriveKey']
        );
        key = await crypto.subtle.deriveKey(
          { name: 'PBKDF2', salt: encoder.encode('salt-for-poc'), iterations: 100000, hash: 'SHA-256' },
          material,
          { name: 'AES-GCM', length: 256 },
          true,
          ['encrypt', 'decrypt']
        );
        setKeyStatus('derived');
      }
      setCryptoKey(key);
    })();
  }, [chatId]);





  // Block filter: redirect if current chat is blocked
  useEffect(() => {
    const storedBlocked = localStorage.getItem('blocked_chats');
    if (storedBlocked) {
      const blocked = JSON.parse(storedBlocked);
      if (blocked.includes(chatId)) {
        // Redirect to home / sidebar
        window.location.href = '/';
        // Optional: show message instead
        // alert('This chat is blocked.');
      }
    }
  }, [chatId]);


// Decrypt
// Decrypt messages + mark my own messages for correct left/right bubbles
  // Decrypt messages + mark my own messages for right/green bubbles
  useEffect(() => {
    if (!cryptoKey || messages.length === 0)  return; 

    const decryptAll = async () => {


      const decrypted = await Promise.all(
        messages
        .filter(msg => msg.chatId === chatId)  // ← only current chat
        .map(async (msg) => {
          let text = "[Decryption failed]";
          let isFromMe = false;

          try {
            // Inline decryption (this is the exact working version)
            const combined = Uint8Array.from(atob(msg.encrypted), c => c.charCodeAt(0));
            const iv = combined.slice(0, 12);
            const data = combined.slice(12);

            const buffer = await crypto.subtle.decrypt(
              { name: 'AES-GCM', iv },
              cryptoKey,
              data
            );

            text = new TextDecoder().decode(buffer);

            // Sender detection for bubbles (this is the important part)
            const senderName = String(
              msg.sender_username || 
              msg.username || 
              msg.from_username || 
              msg.sender || 
              msg.from_user || 
              ''
            );

            isFromMe = senderName.toLowerCase() === (myUsername || '').toLowerCase();

            // This log will tell us exactly why it matches or not
            console.log("Sender detection →", { 
              msgId: msg.id, 
              senderName, 
              myUsername, 
              isFromMe 
            });

          } catch (err) {
            console.error("Decrypt failed for msg", msg.id || "unknown", err.message);
          }


          return {
            ...msg,
            text: decrypted || '[decryption failed]',
            created_at: msg.created_at || msg.timestamp || new Date().toISOString(),  // preserve original timestamp
            sender: msg.sender || 'them'
          };

        })
      );

      setDecryptedMessages(prev => {
        const prevIds = new Set(prev.map(m => m.id));
        const newDecrypted = decrypted.filter(d => !prevIds.has(d.id));
        return [...prev, ...newDecrypted];
      });


    };

    decryptAll();
  }, [messages, cryptoKey, myUsername]);





  // Auto-scroll

// Replace your current auto-scroll useEffect with this
  useEffect(() => {
    if (!messagesEndRef.current)  return;  

    // Check if user is currently at bottom
    const isAtBottom = 
      messagesEndRef.current.getBoundingClientRect().bottom <= 
      (window.innerHeight || document.documentElement.clientHeight) + 50; // small buffer

    // Update ref for next render
    wasAtBottomRef.current = isAtBottom;

    // Scroll conditions:
    // 1. User was at bottom before this update → scroll to new message
    // 2. New messages arrived → scroll (but only if wasAtBottom)
    if (wasAtBottomRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [decryptedMessages]); // trigger on decrypted change (new messages)

  useEffect(() => {
    const checkScroll = () => {
      if (messagesEndRef.current) {
        const rect = messagesEndRef.current.getBoundingClientRect();
        const isAtBottom = rect.bottom <= window.innerHeight + 50;
//        if (isAtBottom) setHasNewMessages(false);
      }
    };
    window.addEventListener('scroll', checkScroll);
    return () => window.removeEventListener('scroll', checkScroll);
  }, []);




  // Polling (extracted as function)
  const pollMessages = async (isManual = false) => {  // optional param to distinguish manual refresh
    try {
      let url = `https://i-msgnet-backend-production.up.railway.app/api/messages/${chatId}`;
      const hasInviteParam = !!inviteKey;

      if (hasInviteParam) {
        url += `?key=${inviteKey}`;
      }

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }

      });


      if (inviteKey) {
        console.log("Poll - clearing inviteKey");
        setInviteKey(null);
        window.history.replaceState({}, '', `/chat/${chatId}`);
      }




      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));

        console.error("Poll FAILED - status:", res.status, "error:", errorData);

        if (res.status === 403 && !hasInviteParam) {
          // Your original comment/log
          console.warn('403 without invite key - possibly not participant anymore');
        }

        if (!isManual) return; // silent fail on auto-poll

        alert('Failed to load messages: ' + (errorData.error || res.status));
        return;
      }


      const remoteMsgs = await res.json();
      // Your existing merge logic here, e.g.:
      // setMessages(prev => [...prev, ...remoteMsgs.filter(r => !prev.some(p => p.id === r.id))]);
      // or just setMessages(remoteMsgs) if you want server-authoritative


      console.log("POLL RESPONSE:", {
        chatId,
        status: res.status,
        inviteKey: !!inviteKey,
        count: remoteMsgs.length,
        timestamps: remoteMsgs.map(m => m.created_at || "no time"),
        firstId: remoteMsgs[0]?.id || "empty"
      });


      console.log("POLL RESPONSE FULL:", {
        chatId,
        status: res.status,
        inviteKeyUsed: !!inviteKey,
        messagesCount: remoteMsgs.length,
        messages: remoteMsgs.map(m => ({
          id: m.id,
          created_at: m.created_at,
          encrypted: m.encrypted ? m.encrypted.substring(0, 50) + '...' : 'no encrypted',
          sender: m.sender || 'unknown'
        }))
      });


      console.log("POLL RESULT:", {
        chatId,
        status: res.status,
        inviteKeyUsed: !!inviteKey,
        messagesCount: remoteMsgs.length,
        firstMsg: remoteMsgs[0] ? JSON.stringify(remoteMsgs[0]) : "empty",
        error: remoteMsgs.error || null
      });

      console.log("Poll response 2:", {
        chatId,
        status: res.status,
        inviteKeyUsed: !!inviteKey,
        messagesCount: remoteMsgs.length,
        firstMessage: remoteMsgs[0] || "empty",
        error: remoteMsgs.error || null
      });


      setMessages(prev => {
        const prevIds = new Set(prev.map(m => m.id));
        const newFromServer = remoteMsgs.filter(r => !prevIds.has(r.id));
        return [...prev, ...newFromServer];
      });



      console.log("Poll response - chatId:", chatId, "messages received:", remoteMsgs.length, "first msg:", remoteMsgs[0] || "empty");

      setMessages(prev => {
        const prevIds = new Set(prev.map(m => m.id));
        const newFromServer = remoteMsgs.filter(r => !prevIds.has(r.id));
        return [...prev, ...newFromServer];
      });




    } catch (err) {
      console.error('Poll error:', err);
      if (isManual) alert('Network error loading messages');
    }
  };



  useEffect(() => {
    console.log('pollMessages useEffect triggered for chatId:', chatId);
    pollMessages();
    const interval = setInterval(pollMessages, 8000);
    return () => clearInterval(interval);
  }, [chatId]);



  useEffect(() => {
    const handleChatSelected = (e) => {
      if (e.detail.chatId === chatId && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };

    window.addEventListener('chatSelected', handleChatSelected);
    return () => window.removeEventListener('chatSelected', handleChatSelected);
  }, [chatId, messages]); // re-attach on chatId change



  useEffect(() => {
    if (inviteKey) {
      console.log('Invite key active:', inviteKey);
      // Clear inviteKey after 5 seconds (or on unmount/chat change)
      const timer = setTimeout(() => {
        setInviteKey(null);
        console.log('Cleared inviteKey after join');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [inviteKey]);



  const copyKey = async () => {
    if (!cryptoKey) return;
    const raw = await crypto.subtle.exportKey('raw', cryptoKey);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(raw)));
    await navigator.clipboard.writeText(base64);
    alert('Key copied! Share securely outside this app.');
  };

  const handleKeyPaste = async (e) => {
    const val = e.target.value.trim();
    setSharedKeyInput(val);
    if (val) {
      const imported = await importKey(val);
      if (imported) {
        setCryptoKey(imported);
        setKeyStatus('shared');
        localStorage.setItem(`key_${chatId}`, val);
      } else {
        setKeyStatus('invalid');
      }
    } else {
      localStorage.removeItem(`key_${chatId}`);
      const encoder = new TextEncoder();
      const material = await crypto.subtle.importKey(
        'raw',
        encoder.encode(chatId + 'fixed-salt-for-poc'),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      );
      const demoKey = await crypto.subtle.deriveKey(
        { name: 'PBKDF2', salt: encoder.encode('salt-for-poc'), iterations: 100000, hash: 'SHA-256' },
        material,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      setCryptoKey(demoKey);
      setKeyStatus('derived');
    }
  };

  const clearKey = () => {
    setSharedKeyInput('');
    localStorage.removeItem(`key_${chatId}`);
    (async () => {
      const encoder = new TextEncoder();
      const material = await crypto.subtle.importKey(
        'raw',
        encoder.encode(chatId + 'fixed-salt-for-poc'),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      );
      const demoKey = await crypto.subtle.deriveKey(
        { name: 'PBKDF2', salt: encoder.encode('salt-for-poc'), iterations: 100000, hash: 'SHA-256' },
        material,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      setCryptoKey(demoKey);
      setKeyStatus('derived');
    })();
  };

  const generateAndSetRandomKey = async () => {
    try {
      const base64Key = await generateKey();
      const imported = await importKey(base64Key);
      if (imported) {
        setCryptoKey(imported);
        setKeyStatus('shared');
        setSharedKeyInput(base64Key);
        localStorage.setItem(`key_${chatId}`, base64Key);
        await navigator.clipboard.writeText(base64Key);
        alert('New secure random key generated and copied!\nShare this securely with your friend.');
      }
    } catch (err) {
      console.error('Random key generation failed:', err);
      alert('Error generating key.');
    }
  };

  const deriveKeyFromPassphrase = async (pass) => {
    if (pass.length < 12) {
      setPassphraseError('Passphrase must be at least 12 characters');
      return;
    }
    try {
      const encoder = new TextEncoder();
      const salt = encoder.encode('i-msgnet-passphrase-salt-2026');
      const material = await crypto.subtle.importKey(
        'raw',
        encoder.encode(pass),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      );
      const derivedKey = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt,
          iterations: 150000,
          hash: 'SHA-256'
        },
        material,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      const exported = await crypto.subtle.exportKey('raw', derivedKey);
      const base64Key = btoa(String.fromCharCode(...new Uint8Array(exported)));
      setCryptoKey(derivedKey);
      setKeyStatus('shared');
      setSharedKeyInput(base64Key);
      localStorage.setItem(`key_${chatId}`, base64Key);
      setPassphraseError('');
      setShowPassphraseInput(false);
      setPassphrase('');
      setShowPassphrase(false);
      alert('Passphrase accepted! Key derived and set.\nYour friend must enter the exact same passphrase.');
    } catch (err) {
      console.error('Passphrase derivation failed:', err);
      setPassphraseError('Failed to derive key – try again');
    }
  };

  // Strength helpers
  const getStrengthColor = (pass) => {
    if (pass.length < 12) return '#dc3545';
    if (pass.length < 16) return '#fd7e14';
    if (pass.length < 20) return '#ffc107';
    return '#28a745';
  };

  const getStrengthWidth = (pass) => {
    const len = Math.min(pass.length, 30);
    return `${(len / 30) * 100}%`;
  };

  const getStrengthLabel = (pass) => {
    if (pass.length < 12) return `Too short (${pass.length}/12)`;
    if (pass.length < 16) return 'Medium';
    if (pass.length < 20) return 'Strong';
    return 'Very strong ✓';
  };

  


   



  const simulateIncoming = async () => {
    if (!cryptoKey) {
      alert('No cryptoKey loaded yet');
      return;
    }
    const fakeTexts = [
      'Hei hei!',
      'Mitäs kuuluu?',
      '😂😂 totta',
      'Sataa taas...',
      'Milloin nähdään?'
    ];
    const text = fakeTexts[Math.floor(Math.random() * fakeTexts.length)];
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, data);
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    const base64 = btoa(String.fromCharCode(...combined));
    const msg = { encrypted: base64, sender: 'them', timestamp: Date.now() };
    const updated = [...messages, msg];
    setMessages(updated);
    localStorage.setItem(`messages_${chatId}`, JSON.stringify(updated));
  };

  const handleSaveName = () => {
    const trimmed = chatNameInput.trim();
    if (trimmed.length < 2) return;
    const storedChats = JSON.parse(localStorage.getItem('chats')) || [];
    const updated = [...storedChats, { id: chatId, name: trimmed }];
    localStorage.setItem('chats', JSON.stringify(updated));
    setShowNamePrompt(false);
    setChatNameInput('');
    window.dispatchEvent(new Event('chatsUpdated'));
  };

  const handleDeleteMessage = async (encrypted) => {
    if (!window.confirm('Delete this message for everyone? This cannot be undone.')) return;
    try {
      const res = await fetch(
        `https://i-msgnet-backend-production.up.railway.app/api/messages/${chatId}/${encodeURIComponent(encrypted)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        pollMessages(); // immediate sync
        setMessages(prev => prev.filter(m => m.encrypted !== encrypted));
        localStorage.setItem(`messages_${chatId}`, JSON.stringify(
          JSON.parse(localStorage.getItem(`messages_${chatId}`) || '[]').filter(m => m.encrypted !== encrypted)
        ));
        pollMessages(); // immediate sync
        alert('Message deleted.');
      } else {
        alert('Failed to delete message on server.');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Error deleting message.');
    }
  };

  return (
    

<React.Fragment>

  console.log("Component rendering - handleSendMessage type:", typeof handleSendMessage);

    {errorBanner && (
    <div style={{ background: '#dc3545', color: 'white', padding: '12px', marginBottom: '16px', borderRadius: '6px' }}>
      {errorBanner}
      <button
       onClick={() => setErrorBanner('')}
       style={{ marginLeft: '16px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
      >
      Close
      </button>
    </div>
)}

    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', padding: '20px', boxSizing: 'border-box' }}>

    {console.log('Inside return - starting JSX')}

      <h2>Chat {chatId.slice(0, 8)}...</h2>


      <a
        href="/faq.html"  // or /faq.html if you rename
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginLeft: '16px',
          color: '#007bff',
          textDecoration: 'none',
          fontSize: '0.95em',
          alignSelf: 'center'
        }}
      >
        FAQ / Usein kysytyt kysymykset
      </a>


      {showNamePrompt && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            padding: '28px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '440px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.5rem' }}>
              Name this conversation
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#555' }}>
              Choose a clear label so you can easily find this chat later in the sidebar.
            </p>

            <input
              type="text"
              value={chatNameInput}
              onChange={(e) => setChatNameInput(e.target.value)}
              placeholder="e.g. Juha / Work friend / Alex"
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                marginBottom: '20px',
                boxSizing: 'border-box',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveName();
                }
              }}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleSaveName}
                disabled={chatNameInput.trim().length < 2}
                style={{
                  padding: '10px 24px',
                  background: chatNameInput.trim().length >= 2 ? '#007bff' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: chatNameInput.trim().length >= 2 ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                }}
              >
                Save name
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <strong>Key status:</strong>
          <span style={{ color: keyStatus === 'shared' ? 'green' : keyStatus === 'invalid' ? 'red' : 'orange' }}>
            {keyStatus === 'shared' ? 'Using shared key ✓' :
             keyStatus === 'derived' ? 'Demo mode (chatId-derived key)' :
             keyStatus === 'invalid' ? 'Invalid key' : 'Loading...'}
          </span>
        </div>





        {keyStatus !== 'shared' && (
          <div style={{ background: '#fff3cd', color: '#856404', padding: '12px', borderRadius: '6px', marginBottom: '12px', fontWeight: 'bold' }}>
            ⚠️ Warning: Secure chat is currently disabled in demo mode. Messages are NOT end-to-end encrypted.
            Paste a shared key from your friend to enable real security. Do NOT send sensitive information until then!
          </div>
        )}

        {keyStatus === 'derived' && (
          <div style={{ margin: '12px 0' }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
              Want to start a secure chat with someone?
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={generateAndSetRandomKey}
                style={{ padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px' }}
              >
                Generate real random key
              </button>
              <button
                onClick={() => setShowPassphraseInput(true)}
                style={{ padding: '10px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '6px' }}
              >
                Use shared passphrase
              </button>
            </div>
          </div>
        )}

        {keyStatus === 'shared' && (
          <small style={{ color: '#28a745', fontWeight: 'bold', display: 'block', margin: '12px 0' }}>
            ✓ Using shared secret key — messages are end-to-end encrypted
          </small>
        )}

        <button onClick={clearKey} style={{ marginTop: '8px', padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
          Clear key / Back to demo
        </button>




        <div style={{ marginTop: '12px' }}>
          <label>Paste shared secret key:</label><br />
          <input
            type="text"
            value={sharedKeyInput}
            onChange={handleKeyPaste}
            placeholder="Paste base64 key here..."
            style={{ width: '100%', padding: '10px', marginTop: '4px', border: '1px solid #ccc', borderRadius: '6px' }}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
            One person creates the key and shares it securely (e.g. via Signal or in person).
            Both must paste the same key here for secure E2EE. Never send the key in this chat!
          </small>
        </div>

        <button
          onClick={() => setShowInviteModal(true)}
          style={{
            marginTop: '16px', // ← more space above
            marginLeft: '8px', // ← space from other key
            marginRight: '12px', // ← space from other key
            padding: '8px 16px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Invite username to new chat
        </button>


        {showPassphraseInput && (
          <div style={{
            marginTop: '16px',
            padding: '16px',
            background: '#e7f3ff',
            borderRadius: '8px',
            border: '1px solid #b3d4fc'
          }}>
            <strong>Enter shared passphrase</strong><br />
            <small style={{ color: '#555', lineHeight: '1.5' }}>
              Both you and your friend must type the <strong>exact same chat passphrase</strong> (minimum 12 characters).<br />
              Agree on it outside this chat (phone, in person, secure message — never type it in this chat!). <br />
              Use <strong>different chat passphrase</strong> than your <strong>account password</strong> - (your friend would know your account password!)
            </small>

            <div style={{ position: 'relative', margin: '16px 0' }}>
              <input
                type={showPassphrase ? 'text' : 'password'}
                value={passphrase}
                onChange={(e) => {
                  setPassphrase(e.target.value);
                  setPassphraseError('');
                }}
                placeholder="Your shared passphrase (min 12 chars)"
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  boxSizing: 'border-box'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    deriveKeyFromPassphrase(passphrase);
                  }
                }}
              />

              <button
                type="button"
                onClick={() => setShowPassphrase(!showPassphrase)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  color: '#555'
                }}
                title={showPassphrase ? 'Hide passphrase' : 'Show passphrase'}
              >
                {showPassphrase ? '🙈' : '👁️'}
              </button>
            </div>

            <div style={{ marginBottom: '12px' }}>
              {passphrase.length > 0 && (
                <>
                  <div style={{
                    height: '8px',
                    background: getStrengthColor(passphrase),
                    borderRadius: '4px',
                    width: getStrengthWidth(passphrase),
                    transition: 'width 0.3s, background 0.3s',
                    marginBottom: '6px'
                  }} />
                  <small style={{
                    fontWeight: 'bold',
                    color: getStrengthColor(passphrase)
                  }}>
                    {getStrengthLabel(passphrase)}
                  </small>
                </>
              )}
            </div>

            {passphraseError && (
              <div style={{ color: '#dc3545', marginBottom: '12px' }}>
                {passphraseError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowPassphraseInput(false);
                  setPassphrase('');
                  setPassphraseError('');
                  setShowPassphrase(false);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => deriveKeyFromPassphrase(passphrase)}
                disabled={passphrase.length < 12}
                style={{
                  padding: '10px 20px',
                  background: passphrase.length >= 12 ? '#007bff' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: passphrase.length >= 12 ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold'
                }}
              >
                Use this passphrase
              </button>
            </div>
          </div>
          
        )}



{showInviteModal && (
  <div style={{
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '400px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
    }}>

      {inviteLink ? (
        <>
          <h3>Invite link ready!</h3>
          <p>Share this link with @{inviteUsername} and open it yourself too (both need to use it to start the chat):</p>
          <p style={{ wordBreak: 'break-all', background: '#f8f9fa', padding: '12px', borderRadius: '6px', margin: '16px 0' }}>
            {inviteLink}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>

            <button
              onClick={() => {
                navigator.clipboard.writeText(inviteLink).then(() => {
                  alert('Link copied to clipboard! Paste it to @' + inviteUsername + ' (keep it secure) and you go to that address too.');
                }).catch(() => {
                  alert('Copy failed — please copy manually.');
                });
              }}
              style={{ padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Copy link
            </button>

            <button
              onClick={() => {
                setShowInviteModal(false);
                setInviteLink('');
                setInviteUsername('');
              }}
              style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Close
            </button>

          </div>
        </>
      ) : (
        <>
          <h3>Invite a username to new chat</h3>
          <input
            type="text"
            value={inviteUsername}
            onChange={(e) => setInviteUsername(e.target.value)}
            placeholder="@username"
            autoFocus  // ← add this
            style={{ width: '100%', padding: '10px', marginBottom: '16px', borderRadius: '6px' }}
          />
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowInviteModal(false);
                setInviteUsername('');
              }}
              style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '6px' }}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!inviteUsername || inviteUsername.length < 5) {
                  alert('Enter a valid username (min 5 chars)');
                  return;
                }
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch('https://i-msgnet-backend-production.up.railway.app/api/invite', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ targetUsername: inviteUsername })
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setInviteLink(data.inviteLink);
                  } else {
                    alert(data.error || 'Error');
                  }
                } catch (err) {
                  alert('Network error');
                }
              }}
              style={{ padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px' }}
            >
              Generate Invite
            </button>
          </div>
        </>
      )}
    </div>
  </div>
  
)}



        {showReportModal && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
            }}>
              <h3 style={{ margin: '0 0 16px 0' }}>Report this chat</h3>
              <p style={{ margin: '0 0 16px 0', color: '#555' }}>
                Please select a reason. Reports are anonymous.
              </p>

              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '12px', borderRadius: '6px' }}
              >
                <option value="">Select reason</option>
                <option value="spam">Spam or unwanted messages</option>
                <option value="abuse">Abusive or harmful content</option>
                <option value="other">Other</option>
              </select>

              <textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Additional details (optional)"
                style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '6px', marginBottom: '16px' }}
              />

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('');
                    setReportDetails('');
                  }}
                  style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!reportReason) {
                      alert('Please select a reason');
                      return;
                    }
                    try {
                      const res = await fetch('https://i-msgnet-backend-production.up.railway.app/api/reports', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ chatId, reason: reportReason, details: reportDetails })
                      });
                      if (res.ok) {
                        alert('Report sent. Thank you!');
                        setShowReportModal(false);
                        setReportReason('');
                        setReportDetails('');
                      } else {
                        alert('Failed to send report');
                      }
                    } catch (err) {
                      console.error('Report failed:', err);
                      alert('Error sending report');
                    }
                  }}
                  disabled={!reportReason}
                  style={{ padding: '10px 20px', background: reportReason ? '#dc3545' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: reportReason ? 'pointer' : 'not-allowed' }}
                >
                  Submit Report
                </button>
              </div>
            </div>
          </div>
        )}


<button onClick={() => alert("Test click works!")}>
  Test Click
</button>



        <button onClick={simulateIncoming} disabled={!cryptoKey} style={{ marginTop: '12px', padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
          Simulate incoming message (test decrypt)
        </button>

        <button
          onClick={() => setShowReportModal(true)}
          style={{
          marginTop: '16px', // ← more space above
          marginLeft: '12px', // ← space from Clear key
          padding: '8px 16px',
          background: '#dc3545',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
          }}
        >
          Report this chat as SPAM
        </button>

        <button
          onClick={() => {
            if (window.confirm('Block this chat? You will not see it again unless unblocked.')) {
              const newBlocked = [...blockedChats, chatId];
              setBlockedChats(newBlocked);
              localStorage.setItem('blocked_chats', JSON.stringify(newBlocked));
              // Optional: navigate back to sidebar
              window.location.href = '/';
            }
          }}
          style={{
            marginTop: '16px',
            marginLeft: '12px',
            padding: '8px 16px',
            background: '#6c757d', // gray for block
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Block this chat
        </button>

      </div>




      <button
        onClick={async () => {
          setIsReloading(true);
          localStorage.removeItem(`messages_${chatId}`);
          await pollMessages();
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
          setIsReloading(false);
        }}
        disabled={isReloading}
        style={{
          marginLeft: '16px',
          padding: '6px 12px',
          background: isReloading ? '#6c757d' : '#6c757d', // gray for secondary look
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: isReloading ? 'not-allowed' : 'pointer',
          fontSize: '13px',
          minWidth: '140px'
        }}
      >
        {isReloading ? 'Checking...' : 'Check new messages'}
      </button>




{/* padding: '20px 40px', dustbins location */}
{/* <div style={{ flex: 1, overflowY: 'auto', overflowX: 'visible', padding: '10px 0', display: 'flex', flexDirection: 'column', position: 'relative' }}> */}

        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'visible',
          padding: '0px 20px',  // ← more side padding
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box'
        }}>


          {decryptedMessages
          .filter(msg => msg.chatId === chatId)   // only show messages for current chat
          .map((msg, idx) => (

          <div
            key={idx}
            className="message-bubble"
            style={{
              alignSelf: msg.sender === 'me' ? 'flex-end' : 'flex-start',
              background: msg.sender === 'me' ? '#dcf8c6' : '#e5e5ea',  // green for me, light for them
              maxWidth: '70%',
              margin: '16px 0',
              padding: '12px 20px',
              borderRadius: '18px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              wordBreak: 'break-word',
              position: 'relative',
              overflow: 'visible'
            }}
          >
            {msg.text}
            <div style={{
              fontSize: '0.75em',
              opacity: 0.7,
              marginTop: '4px',
              textAlign: msg.sender === 'me' ? 'right' : 'left'
            }}>
              {formatMessageTime(msg.serverTimestamp || msg.timestamp || Date.now())}
            </div>

            <button
              onClick={() => handleDeleteMessage(msg.encrypted)}
              className="delete-btn"
              style={{
                position: 'absolute',
                top: '-18px',

/* dustbin locations */
                right: msg.sender === 'me' ? '2px' : 'auto', 
                left: msg.sender === 'them' ? '-0px' : 'auto', 
/*                right: msg.sender === 'me' ? '-28px' : 'auto', */
/*                left: msg.sender === 'them' ? '-28px' : 'auto',  */

                background: 'rgba(255,255,255,0.9)',
                border: '1px solid #dc3545',
                borderRadius: '50%',
                color: '#dc3545',
                fontSize: '1.2em',
                cursor: 'pointer',
                padding: '4px',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                opacity: 0,
                transition: 'opacity 0.2s'
              }}
              title="Delete message"
            >
              🗑
            </button>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ display: 'flex', paddingTop: '10px', borderTop: '1px solid #eee' }}>
        <div style={{ marginBottom: '12px', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold' }}>Delete after:</label>
          <label>
            <input
              type="radio"
              name="lifespan"
              value="24"
              checked={lifespanHours === '24'}
              onChange={() => setLifespanHours('24')}
            /> 24 hours
          </label>
          <label>
            <input
              type="radio"
              name="lifespan"
              value="192"
              checked={lifespanHours === '192'}
              onChange={() => setLifespanHours('192')}
            /> 8 days
          </label>
          <label>
            <input
              type="radio"
              name="lifespan"
              value="null"
              checked={lifespanHours === 'null'}
              onChange={() => setLifespanHours('null')}
            /> No limit
          </label>
        </div>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message"
          style={{ flex: 1, padding: '12px', border: '1px solid #ccc', borderRadius: '20px' }}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
        />
        <button
          type="button"
          onClick={handleSendMessage}  // ← direct reference, no arrow
            style={{ marginLeft: '10px', padding: '12px 24px', background: '#25D366', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer' }}
        >
          Send It
        </button>
      </div>
    </div>


</React.Fragment>

  );
  
}

export default PrivateChat;